<template>
  <div id="app"
       class="app"
       :class="[className, getPlatformInfo]">
    <alert-sticky-bar/>
    <component :is="layoutName"
      @click.native="hideAllLayers"/>
    <dialogs-wrapper tag="div"></dialogs-wrapper>
    <dialogs-wrapper tag="div" name="error"></dialogs-wrapper>
  </div>
</template>

<script>
// must import app.scss first for correct css ordering
import '@/styles/app.scss'
import { SIGNIN_LINK } from '@/libs/constants'
import { mapState, mapMutations } from 'vuex'
import { create } from 'vue-modal-dialogs'
import { STORAGE_KEY, storageManager } from '@/libs/storage-manager'
import {
  getAuthToken,
  getAuthCookie,
  removeAuthData
} from '@/libs/auth-helper'
import { clearAllApiCache } from '@/libs/api-request'
import DefaultLayout from '@/layouts/DefaultLayout'
import ReportLayout from '@/layouts/ReportLayout'
import DashboardLayout from '@/layouts/DashboardLayout'
import PageLayout from '@/layouts/PageLayout'
import NoHeaderLayout from '@/layouts/NoHeaderLayout'
import EmptyLayout from '@/layouts/EmptyLayout'
import AppDialog from '@/components/ui/AppDialog'
import AlertStickyBar from '@/components/header/AlertStickyBar'
import { browserInfo } from '@/libs/utils'

const errorDialog = create({
  component: AppDialog,
  wrapper: 'error',
  props: ['dialogType', 'ok']
})

export default {
  name: 'App',
  components: {
    AlertStickyBar,
    AppDialog,
    'default-layout': DefaultLayout,
    'report-layout': ReportLayout,
    'dashboard-layout': DashboardLayout,
    'page-layout': PageLayout,
    'no-header-layout': NoHeaderLayout,
    'empty-layout': EmptyLayout
  },
  data () {
    return {
      hasError: false // prevent multiple error dialog
    }
  },
  computed: {
    ...mapState([
      'userData',
      'errorData',
      'isDashboardEditMode',
      'stickyDataId',
      'showStickyData',
      'myInfoTimestamp',
      'showNoPvDialog',
      'dreamVersion'
    ]),
    getPlatformInfo () {
      return 'platform__' + browserInfo.platform
    },
    layoutName () {
      if (!this.$route.matched.length) {
        return null
      }
      const route = Array.from(this.$route.matched).reverse()
        .find(item => item.meta.hasOwnProperty('layout'))

      const layout = route ? route.meta.layout : 'default'
      return `${layout}-layout`
    },
    className () {
      const items = this.$route ? [`route-${this.$route.name}`] : ''

      if (this.$route.meta.appClass) {
        items.push(this.$route.meta.appClass)
      }

      if (items.length) {
        return items.join(' ')
      }
      return null
    }
  },
  watch: {
    $route: {
      handler (current, prev) {
        // this.syncAuthTokenAndCookie()
        const guestOnly = current.meta.guestOnly
        const authToken = getAuthToken()

        if (guestOnly) {
          if (authToken) {
            this.$router.replace({ name: 'dashboard' })
          }
        } else {
          if (!authToken) {
            this.signOut()
            window.location.href = SIGNIN_LINK
          }
        }
      },
      immediate: true
    },
    errorData (value) {
      if (value && !this.hasError) {
        this.hasError = true
        errorDialog('common.error', this.$t('common.confirm')).then(() => {
          this.setErrorData(null)
          this.hasError = false
        })
      }
    },
    dreamVersion (current, prev) {
      if (current && prev && current > prev) {
        this.$nextTick().then(() => {
          window.location.reload(true)
        })
      }
    }
  },
  created () {
    // this.syncTimer = window.setInterval(() => {
    //   this.syncAuthTokenAndCookie()
    // }, 1000)
    window.addEventListener('beforeunload', event => {
      if (this.$route.name === 'dashboard' && this.isDashboardEditMode) {
        event.preventDefault()
        event.returnValue = this.$t('common.windowUnload')
        // todo: check IE
        return event.returnValue
      }

      if (this.userData) {
        storageManager.setItem(STORAGE_KEY.showNoPvDialog, this.showNoPvDialog)
        storageManager.setItem(STORAGE_KEY.stickyDataId, this.stickyDataId)
        storageManager.setItem(STORAGE_KEY.showStickyData, this.showStickyData)
        storageManager.setItem(STORAGE_KEY.myInfoTimestamp,
          this.myInfoTimestamp)
      }
    })
  },
  destroyed () {
    window.clearInterval(this.syncTimer)
  },
  methods: {
    ...mapMutations([
      'setShowUserMenu',
      'setShowReportPageList',
      'setErrorData',
      'clearAllUserData'
    ]),
    // to-do: use single mutation
    hideAllLayers () {
      this.setShowUserMenu(false)
      this.setShowReportPageList(false)
    },
    syncAuthTokenAndCookie () {
      const authToken = getAuthToken()
      const authCookie = getAuthCookie()

      if (authToken && !authCookie) {
        removeAuthData()
      }
    },
    signOut () {
      if (this.userData) {
        storageManager.initUser(this.userData.userNum)
        storageManager.removeItem(STORAGE_KEY.showNoPvDialog)
        storageManager.removeItem(STORAGE_KEY.stickyDataId)
        storageManager.removeItem(STORAGE_KEY.showStickyData)
        storageManager.removeItem(STORAGE_KEY.myInfoTimestamp)
      }

      clearAllApiCache()
      this.clearAllUserData()
      removeAuthData()
    }
  }
}
</script>

<style lang="scss">
</style>
